import { ReactNode, lazy, Suspense, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { toLogicalPath } from '@/i18n/locale'
import { useLocaleMeta } from '@/i18n/useLocale'
import { Header } from './Header'
import { OfferHeader } from './OfferHeader'
import { Footer } from './Footer'
import { WhatsAppFab } from './WhatsAppFab'

// Defer chat bundle to improve initial load. Header/Footer/OfferHeader are NOT
// lazy: they render on every route, and lazy-loading them (React.lazy + Suspense
// fallback=null) rendered empty on the server but real content on the client ,
// a hydration mismatch (React error #418/#423) that made React throw away the
// entire prerendered tree and do a full client-only re-render on every page load.
const LazyAssistantChat = lazy(() => import('@/components/chat/AssistantChat').then(m => ({ default: m.AssistantChat })))

function DeferredChatLauncher({ onOpen }: { onOpen: () => void }) {
  const [mounted, setMounted] = useState(false)
  const [showHint, setShowHint] = useState(false)

  useEffect(() => {
    setMounted(true)
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    const showTimer = setTimeout(() => {
      setShowHint(true)
      hideTimer = setTimeout(() => setShowHint(false), 6000)
    }, 5000)

    return () => {
      clearTimeout(showTimer)
      if (hideTimer) clearTimeout(hideTimer)
    }
  }, [])

  if (!mounted) return null

  return createPortal(
    <>
      {showHint && (
        <div className="pointer-events-none fixed bottom-[90px] right-5 z-[10000] whitespace-nowrap rounded-lg bg-fw-ink px-4 py-2.5 text-sm font-medium text-fw-white shadow-xl">
          Questions about AI automation?
          <span
            className="absolute right-6 top-full border-x-[6px] border-t-[6px] border-x-transparent border-t-fw-ink"
            aria-hidden
          />
        </div>
      )}
      <WhatsAppFab className="fixed bottom-[84px] right-5 z-[9998]" />
      <button
        type="button"
        aria-label="Open chat"
        onClick={onOpen}
        onMouseEnter={() => setShowHint(true)}
        onMouseLeave={() => setShowHint(false)}
        className="fixed bottom-5 right-5 z-[9999] flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-[3px] border-white bg-white shadow-[0_0_0_2px_#6941E8,0_10px_25px_rgba(10,23,68,0.28)] transition-transform hover:scale-105 sm:h-14 sm:w-14"
      >
        <img
          src="/images/chatbot-support-agent.avif"
          alt=""
          className="h-full w-full rounded-full object-cover object-top"
          width="56"
          height="56"
          loading="lazy"
          decoding="async"
        />
      </button>
    </>,
    document.body,
  )
}

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { htmlLang, dir } = useLocaleMeta()
  const logicalPath = toLogicalPath(location.pathname)
  const [showChat, setShowChat] = useState(false)

  // Hide navbar on Discover, WorkshopKM, and DoctorsAgent pages
  const isWorkshopKM = logicalPath === '/workshop-km'
  const isDoctorsAgent = logicalPath.startsWith('/doctors-agent')
  const isDoctorsAgent2 = logicalPath.startsWith('/doctors-agent-2')
  const isDoctorsFamily = isDoctorsAgent || isDoctorsAgent2
  const isDentaire = logicalPath === '/dentaire'
  const isDoctorsDmGuide = logicalPath.startsWith('/automations/convertir-vos-dm-en-rdv')
  const isDoctorOnboarding = logicalPath === '/configurer-assistant'
  // Booking page has its own WhatsApp fallback + sticky CTA in the FAB's spot
  const isDemoBooking = logicalPath === '/doctors-agent/rdv'
  const hideNavbar = logicalPath === '/discover' || isWorkshopKM || isDoctorsFamily || isDoctorsDmGuide || isDoctorOnboarding
  const isOfferPage = logicalPath === '/offer'

  const pageBgColor = isDoctorsDmGuide ? '#060B08' : (isWorkshopKM || isDoctorsFamily) ? 'transparent' : '#FCFCFD'
  // Hide footer on WorkshopKM and funnel landings (full-screen style)
  const hideFooter = isWorkshopKM || isDoctorsFamily || isDoctorsDmGuide || isDoctorOnboarding

  // Hide chat on Doctors Agent pages
  const hideChat = isDoctorsFamily || isDentaire || isDoctorsDmGuide || isDoctorOnboarding

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" style={{ margin: 0, padding: 0, backgroundColor: isWorkshopKM ? 'transparent' : pageBgColor }}>
      <Head>
        <html lang={htmlLang} dir={dir} />
      </Head>
      {!hideNavbar && (isOfferPage ? <OfferHeader hasPromoBar={false} /> : <Header hasPromoBar={false} />)}
      <main className="flex-grow relative" style={{ paddingTop: 0, marginTop: 0, padding: 0, margin: 0, width: '100%', backgroundColor: isWorkshopKM ? 'transparent' : pageBgColor }}>
        {children}
      </main>
      {!hideFooter && <Footer />}
      {/* Sticky Chat Widget - Available on all pages except excluded ones */}
      {!showChat && !hideChat && <DeferredChatLauncher onOpen={() => setShowChat(true)} />}
      {showChat && !hideChat && (
        <Suspense fallback={null}>
          <LazyAssistantChat showWhatsAppLauncher initialOpen />
        </Suspense>
      )}
      {hideChat && !isDoctorsDmGuide && !isDoctorOnboarding && !isDemoBooking && <WhatsAppFab fixed />}
    </div>
  )
}
